/**
 * Reloads stale app shells after Vite fails to preload a dynamic import.
 */
const STALE_DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk \S+ failed/i,
];

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "";
};

export const isStaleDynamicImportError = (error: unknown): boolean => {
  const errorMessage = getErrorMessage(error);

  return STALE_DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) =>
    pattern.test(errorMessage),
  );
};

const RELOAD_GUARD_KEY = "staleDynamicImportReloadAt";
const RELOAD_COOLDOWN_MS = 10_000;

export const reloadPageForStaleDynamicImportError = (
  error: unknown,
  reloadPage = () => window.location.reload(),
): boolean => {
  if (!isStaleDynamicImportError(error)) {
    return false;
  }

  const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
  if (Date.now() - last < RELOAD_COOLDOWN_MS) {
    return false;
  }

  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  reloadPage();
  return true;
};

export const setupVitePreloadErrorHandler = (
  reloadPage = () => window.location.reload(),
): (() => void) => {
  const handlePreloadError = (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    reloadPageForStaleDynamicImportError(payload, reloadPage);
  };

  window.addEventListener("vite:preloadError", handlePreloadError);

  return () => {
    window.removeEventListener("vite:preloadError", handlePreloadError);
  };
};
